/* eslint-disable no-unused-expressions */
/* eslint-disable no-restricted-globals */
import React, { useState, useEffect, Fragment } from 'react';
import { Link, withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import {
  Drawer, Select, Table, Tag,
} from 'antd';
import { Helmet } from 'react-helmet';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import searchIcon from '@Images/icons/icon-search.png';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined, LoadingOutlined,
} from '@ant-design/icons';
import Helpers from '@Apis/helpers';
import { H3_TITLE, toISTDate } from '@Apis/constants';
import H3Button, { defaultButtonTypes } from '@Uilib/h3Button';
import H3Text from '@Uilib/h3Text';
import H3Image from '@Uilib/h3Image';
import ForecastForm from '@Components/Forecasting/ForecastForm';
import ForecastActions from '@Actions/application/forecastActions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRightLong, faFileCsv } from '@fortawesome/free-solid-svg-icons';
import closeIcon from '@Images/icons/icon-close-blue.png';
import './style.scss';
import PRZSelect from '../../Common/UI/PRZSelect';

const { Option } = Select;

const getDatesDiff = (fromDate, toDate) => {
  let diff = '';
  const diffInDays = dayjs(toDate).diff(dayjs(fromDate), 'day', false);
  if (diffInDays > 360) {
    diff = `${Number(dayjs(toDate).diff(dayjs(fromDate), 'year', true).toFixed(1))} Years`;
  }
  if (diffInDays > 180 && diffInDays <= 360) {
    diff = `${Number(dayjs(toDate).diff(dayjs(fromDate), 'month', true).toFixed(1))} Months`;
  }
  if (diffInDays > 28 && diffInDays <= 180) {
    diff = `${Number(dayjs(toDate).diff(dayjs(fromDate), 'week', true).toFixed(1))} Weeks`;
  }
  if (diffInDays > 0 && diffInDays <= 28) {
    diff = `${Number(dayjs(toDate).diff(dayjs(fromDate), 'day', true).toFixed(1))} Days`;
  }
  return diff;
};
/**
 *
 */
function ForecastList({
  user, history, getForecastByIdSuccess, getForecastList, getForecastListLoading, forecasts,
}) {
  const [keyword, setKeyword] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [selectedStatus, setSelectedStatus] = useState(['DATA_PROCESSED', 'DATA_PROCESSING_FAILED', 'TRAINING_FAILED', 'TRAINED']);
  const [isUserReady, setIsUserReady] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [showForecastForm, setShowForecastForm] = useState(false);

  const getStatus = (st) => {
    if (st === 'DATA_PROCESSING_FAILED') return { tag: <Tag icon={<CloseCircleOutlined />} color="red">Processing Error</Tag> };
    if (st === 'DATA_PROCESSED') return { tag: <Tag icon={<ClockCircleOutlined />} color="blue">Processed</Tag> };
    if (st === 'TRAINING_FAILED') return { tag: <Tag icon={<CloseCircleOutlined />} color="red">Forecasting Error</Tag> };
    if (st === 'TRAINED') return { tag: <Tag icon={<CheckCircleOutlined />} color="green">Completed</Tag> };
    if (['FILE_READ_FROM_BLOB', 'TRAINING_STARTED', 'PRE_PROCESSING_FOR_TRAINING', 'EVALUATION_DONE'].includes(st)) return { tag: <Tag icon={<LoadingOutlined />} color="blue">Forecasting In Progress</Tag> };
    return { tag: <Tag color="blue">{st}</Tag> };
  };

  const getModelName = (name) => {
    if (name === 'xgb_model') return 'XGBoost';
    if (name === 'lasso') return 'Lasso Model';
    if (name === 'linear_regression') return 'Linear Regression';
    if (name === 'ridge') return 'Ridge Model';
    return name;
  };
  const getForecastColumns = () => {
    const forecastColumns = [
      {
        title: '#',
        responsive: ['sm', 'md', 'lg', 'xxl'],
        render: (record) => `#${record?.forecast_number}`,
        fixed: 'left',
      },
      {
        title: 'Name',
        responsive: ['sm', 'md', 'lg', 'xxl'],
        render: (record) => (
          <div>
            <H3Text text={record?.forecast_name?.length > 20 ? `${record?.forecast_name?.substring(0, 20)}..` : record?.forecast_name} className="" />
          </div>
        ),
      },
      {
        title: 'Source',
        responsive: ['sm', 'md', 'lg', 'xxl'],
        render: (record) => (
          <div>
            <FontAwesomeIcon icon={faFileCsv} />
            {' '}
            CSV/XLSX
          </div>
        ),
      },
      {
        title: 'Input Date Duration',
        responsive: ['sm', 'md', 'lg', 'xxl'],
        render: (text, record) => (
          <Fragment>
            {(record?.input_date_from && record?.input_date_to) ? (
              <Fragment>
                <H3Text
                  text={getDatesDiff(toISTDate(record?.input_date_from), toISTDate(record?.input_date_to))}
                  className="forecast-list__date-diff"
                />
                <div className="forecast-list__date-range">
                  {toISTDate(record?.input_date_from)?.format('DD MMM YYYY')}
                &nbsp;
                  <FontAwesomeIcon icon={faArrowRightLong} />
                &nbsp;
                  {toISTDate(record?.input_date_to)?.format('DD MMM YYYY')}
                </div>
              </Fragment>
            ) : '-'}
          </Fragment>
        ),
      },
      {
        title: 'Forecast Duration',
        responsive: ['sm', 'md', 'lg', 'xxl'],
        render: (text, record) => (
          <Fragment>
            {
              (record?.input_date_from && record?.input_date_to) ? (
                <Fragment>
                  <H3Text
                    text={getDatesDiff(toISTDate(record?.input_date_to)?.add(1, 'day'), toISTDate(record?.input_date_to)?.add(record?.forecast_duration_in_days + 1, 'day'))}
                    className="forecast-list__date-diff"
                  />
                  <div className="forecast-list__date-range">
                    {toISTDate(record?.input_date_to)?.add(1, 'day')?.format('DD MMM YYYY')}
                    &nbsp;
                    <FontAwesomeIcon icon={faArrowRightLong} />
                    &nbsp;
                    {toISTDate(record?.input_date_to)?.add(record?.forecast_duration_in_days + 1, 'day')?.format('DD MMM YYYY')}
                  </div>
                </Fragment>
              ) : '-'
            }
          </Fragment>
        ),
      },
      {
        title: 'Accuracy',
        responsive: ['sm', 'md', 'lg', 'xxl'],
        width: '150px',
        render: (text, record) => {
          let lowestPair = null;
          let accuracy = 0;
          if (record?.model_percentage_errors) {
            for (const key in record?.model_percentage_errors) {
              if (lowestPair === null || record?.model_percentage_errors[key] < lowestPair?.value) {
                lowestPair = { key, value: record?.model_percentage_errors[key] };
              }
            }
            accuracy = Number((100 - Number(lowestPair?.value))?.toFixed(2));
          }

          return (
            <div style={{ paddingRight: '20px' }}>
              {record?.model_percentage_errors ? (
                <div>
                  <div className="forecast-list__accuracy">
                    <div className="flex-display">
                      <H3Text text={getModelName(lowestPair?.key)} className="forecast-list__accuracy-name" />
                      <H3Text text={`${accuracy}%`} className="forecast-list__accuracy-value" />
                    </div>
                    <div
                      className="forecast-list__accuracy-line"
                      style={{
                        width: '130px',
                        backgroundColor: accuracy < 25 ? 'rgba(216,51,15,0.2)' : accuracy > 75 ? 'rgba(23,181,77,0.2)' : 'rgba(204,185,10,0.2)',
                      }}
                    >
                      <div
                        className="forecast-list__accuracy-line-filled"
                        style={{
                          width: `${accuracy > 100 ? 100 : accuracy}%`,
                          backgroundColor: accuracy < 25 ? '#D8330F' : accuracy > 75 ? '#17B54D' : '#CCB90A',
                        }}
                      />
                    </div>
                  </div>
                </div>
              ) : '-'}
            </div>
          );
        },
      },
      {
        title: 'Status',
        responsive: ['sm', 'md', 'lg', 'xxl'],
        render: (item) => (getStatus(item.status)?.tag),
      },
      {
        title: 'Date',
        responsive: ['sm', 'md', 'lg', 'xxl'],
        render: (text, record) => (
          <div>
            {toISTDate(record?.created_at).format('DD/MM/YYYY')}
            {record?.created_by_info && (
              <H3Text
                text={(record?.created_by_info ? `${record?.created_by_info.first_name} ${record?.created_by_info.last_name}` : 'Unknown')}
                className="table-subscript"
              />
            )}
          </div>
        ),
      },
    ];
    forecastColumns.push({
      title: '',
      responsive: ['sm', 'md', 'lg', 'xxl'],
      key: 'action',
      className: 'list__actions-column',
      fixed: 'right',
      render: (text, record) => (
        <div className="list__actions">
          <div
            style={{ width: '100%' }}
            className="list__actions-button list__actions-button-dark"
            onClick={() => {
              getForecastByIdSuccess(null);
              history.push(`/analytics/forecasting/create/${record?.forecast_id}`);
            }}
          >
            View
          </div>
        </div>
      ),
    });
    return forecastColumns;
  };

  const pushParamsToUrl = (page, lt, status, word) => {
    let url = '';
    if (page) url += `?page=${page}`;
    if (lt) url += `&limit=${lt}`;
    if (status) url += `&status=${status}`;
    if (word) url += `&keyword=${word}`;
    history.push(url);
  };

  useEffect(() => {
    if (user?.tenant_info?.org_id) {
      setCurrentPage(Number(Helpers.getValueFromUrl('page') || currentPage));
      setLimit(Number(Helpers.getValueFromUrl('limit') || limit));
      setSelectedStatus(Helpers.getValueFromUrl('status') ? Helpers.getValueFromUrl('status')?.split(',') : []);
      setSearchKeyword(Helpers.getValueFromUrl('keyword'));
      setKeyword(Helpers.getValueFromUrl('keyword'));
      setIsUserReady(true);
    }
  }, []);

  useEffect(() => {
    if (isUserReady) {
      pushParamsToUrl(currentPage, limit, selectedStatus, searchKeyword);
      getForecastList({
        status: selectedStatus,
        orgId: user?.tenant_info?.org_id,
        page: currentPage,
        limit,
        searchKeyword: keyword,
      }, () => {});
    }
  }, [limit, searchKeyword, currentPage, selectedStatus]);

  /**
   *
   */
  return (
    <Fragment>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{H3_TITLE}</title>
      </Helmet>
      <div className="forecast-list__wrapper">
        <div>
          <div className="forecast-list__header">
            <div className="section-search" style={{ width: '400px' }}>
              <div className="section-search-bar__wrapper">
                <div className="section-search-bar">
                  <input
                    value={keyword}
                    placeholder="Search for forecast..."
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        setSearchKeyword(e.target.value);
                        setCurrentPage(1);
                      }
                    }}
                    onChange={(event) => {
                      setKeyword(event.target.value);
                    }}
                  />
                  <H3Image
                    src={searchIcon}
                    className="section-search-bar__icon"
                    alt="search"
                    onClick={() => {
                      setSearchKeyword(keyword);
                      setCurrentPage(1);
                    }}
                  />
                </div>
              </div>
            </div>
            <div className="forecast-list__header-right">
              <div className="filter__container filter__container-section">
                <PRZSelect
                  value={selectedStatus}
                  onChange={(val) => setSelectedStatus(val)}
                  mode="multiple"
                  maxTagCount="responsive"
                  showSearch={false}
                  style={{ width: '200px', height: '32px', marginTop: '3px' }}
                  placeholder="Filter by status.."
                >
                  <Option value="DATA_PROCESSING_FAILED">Processing Failed</Option>
                  <Option value="DATA_PROCESSED">Processed</Option>
                  <Option value="TRAINING_FAILED">Training Failed</Option>
                  <Option value="TRAINED">Trained</Option>
                </PRZSelect>
              </div>
              {Helpers.getPermission(Helpers.permissionEntities.USER_MANAGEMENT, Helpers.permissionTypes.CREATE, user) && (
                <Link to="/analytics/forecasting/create">
                  <H3Button
                    buttonType={defaultButtonTypes.BLUE_ROUNDED}
                    text="+ New Forecast"
                    onClick={() => {
                      getForecastByIdSuccess(null);
                    }}
                    customClass="forecast-list__add-button"
                  />
                </Link>
              )}
            </div>
          </div>
          {(searchKeyword || selectedStatus) && (
            <div className="header__tags" style={{ marginTop: '15px' }}>
              {searchKeyword && (
                <Tag
                  closable
                  onClose={() => {
                    setSearchKeyword('');
                    setKeyword('');
                    setCurrentPage(1);
                  }}
                  color="#2d7df7"
                >
                  Search results for -
                  &nbsp;
                  <span><b><i>{searchKeyword}</i></b></span>
                </Tag>
              )}
              {selectedStatus && selectedStatus?.map((item, index) => (
                <Tag
                  closable
                  onClose={() => setSelectedStatus(selectedStatus?.filter((tId) => tId !== item))}
                  color="#2d7df7"
                >
                  {' '}
                  Status -
                  &nbsp;
                  <span><b>{item?.replaceAll('_', ' ').toUpperCase()}</b></span>
                  {' '}
                </Tag>
              ))}
            </div>
          )}
        </div>
        <div className="ant-row">
          <div className="ant-col-md-24">
            <Table
              title={() => (
                <H3Text
                  text={(
                    <span>
                      {`${forecasts?.count || '0'} Recent Forecasts`}
                    </span>
                  )}
                  className="forecast-list__total"
                />
              )}
              showHeader
              loading={getForecastListLoading}
              size="small"
              scroll={{ x: 'max-content' }}
              columns={getForecastColumns()}
              dataSource={forecasts?.data || []}
              currentPage
              pagination={forecasts?.count > 10 ? {
                defaultCurrent: 1,
                current: currentPage,
                total: forecasts ? forecasts?.count : 0,
                pageSize: limit,

                position: ['bottomLeft'],
                showSizeChanger: false,
                showTotal: (tot, range) => `${range[0]}-${range[1]} of ${tot} items`,
                onChange: (page, pageSize) => {
                  setCurrentPage(page);
                  setLimit(pageSize);
                },
              } : false}
              bordered
            />
          </div>
        </div>
      </div>
      <Drawer
        open={showForecastForm}
        width="700px"
        mask
        onClose={() => {
          setShowForecastForm(false);
          getForecastByIdSuccess(null);
        }}
        destroyOnClose
      >
        <div className="custom-drawer__header-wrapper">
          <div className="custom-drawer__header" style={{ width: '655px' }}>
            <H3Text text="Create New Forecast" className="custom-drawer__title" />
            <H3Image src={closeIcon} className="custom-drawer__close-icon" onClick={() => setShowForecastForm(false)} />
          </div>
        </div>
        <ForecastForm />
      </Drawer>
    </Fragment>
  );
}

const mapStateToProps = ({
  UserReducers, ForecastReducers,
}) => ({
  user: UserReducers.user,
  getForecastListLoading: ForecastReducers.getForecastListLoading,
  forecasts: ForecastReducers.forecasts,
});

const mapDispatchToProps = (dispatch) => ({
  getForecastByIdSuccess: (selectedForecast) => dispatch(ForecastActions.getForecastByIdSuccess(selectedForecast)),
  getForecastList: (payload, callback) => dispatch(ForecastActions.getForecastList(payload, callback)),
});

ForecastList.propTypes = {
  user: PropTypes.any,
  history: PropTypes.any,
  getForecastList: PropTypes.func,
  getForecastListLoading: PropTypes.bool,
  forecasts: PropTypes.any,
  getForecastByIdSuccess: PropTypes.func,

};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(ForecastList));
